/* eslint-disable max-lines */
import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnFiltersState } from '@tanstack/react-table';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import { Plus, Trash2, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';

import Button from '@components/atoms/ui-kit/button';
import Loader from '@components/atoms/loaders/loader';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { type GridAction } from '@components/atoms/datatable/grid-action-controller';
import AddOneOffModal from './add-one-off-modal';

import {
  useGetOneOffCharges,
  useDeleteOneOffCharge
} from '@utils/api/back-office/usage-revenue/use-one-off-lines';
import { OneOffCharge } from '@utils/types/back-office/usage-revenue';
import { BaseError } from '@utils/types/shared/base';
import { formatDate } from '@utils/date';
import { formatCurrency } from '@utils/helpers/currency';
import { SupportedCurrency } from '@utils/types/organization/organization';

type Row = {
  id: number;
  organization_id: number;
  organization_name: string;
  description: string;
  amount_excl: number;
  invoiced: boolean;
  created_by: string;
  created_at: string;
};

interface OneOffChargesListProps {
  currency?: SupportedCurrency;
}

export default function OneOffChargesList({
  currency = 'EUR'
}: OneOffChargesListProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // Server-side state
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState<string>('created_at');
  const [direction, setDirection] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [descriptionQ, setDescriptionQ] = useState<string | undefined>(
    undefined
  );
  const [createdByQ, setCreatedByQ] = useState<string | undefined>(undefined);
  const [minAmount, setMinAmount] = useState<number | undefined>(undefined);
  const [invoicedFilter, setInvoicedFilter] = useState<boolean | undefined>(
    undefined
  );

  const PAGE_SIZE = 25;

  const {
    data: chargesData,
    isLoading,
    refetch
  } = useGetOneOffCharges({
    sort,
    direction,
    search,
    descriptionQ,
    createdByQ,
    minAmount,
    invoiced: invoicedFilter,
    limit: PAGE_SIZE,
    offset
  });
  const { mutateAsync: deleteCharge } = useDeleteOneOffCharge();

  const BE_SORT_FIELDS: Record<string, string> = {
    organization_name: 'organization_name',
    description: 'description',
    amount_excl: 'amount_excl',
    created_by: 'created_by',
    created_at: 'created_at'
  };

  const handlePaginationChange = useCallback(
    (pagination: { pageIndex: number; pageSize: number }) => {
      setOffset(pagination.pageIndex * pagination.pageSize);
    },
    []
  );

  const handleSortChange = useCallback(
    (sortingState: Array<{ id: string; desc: boolean }>) => {
      if (sortingState.length === 0) {
        setSort('created_at');
        setDirection('desc');
        return;
      }
      const mapped = BE_SORT_FIELDS[sortingState[0].id] ?? 'created_at';
      setSort(mapped);
      setDirection(sortingState[0].desc ? 'desc' : 'asc');
    },
    []
  );

  const handleFilterChange = useCallback((filters: ColumnFiltersState) => {
    const find = (id: string) => filters.find(f => f.id === id)?.value;
    setSearch(
      find('organization_name') ? String(find('organization_name')) : undefined
    );
    setDescriptionQ(
      find('description') ? String(find('description')) : undefined
    );
    setCreatedByQ(find('created_by') ? String(find('created_by')) : undefined);
    setMinAmount(
      find('amount_excl') != null ? Number(find('amount_excl')) : undefined
    );
    const inv = find('invoiced');
    if (inv === 'true') setInvoicedFilter(true);
    else if (inv === 'false') setInvoicedFilter(false);
    else setInvoicedFilter(undefined);
    setOffset(0);
  }, []);

  const rows = useMemo<Row[]>(() => {
    if (!chargesData?.result) return [];
    return chargesData.result.map((charge: OneOffCharge) => ({
      id: charge.id,
      organization_id: charge.organization_id,
      organization_name: charge.organization_name,
      description: charge.description,
      amount_excl: Number(charge.amount_excl) || 0,
      invoiced: charge.invoice_line_id != null,
      created_by: charge.created_by,
      created_at: charge.created_at // raw ISO string — formatted in cell for correct sort
    }));
  }, [chargesData]);

  // Calculate totals
  const { pendingTotal, pendingCount, invoicedTotal, invoicedCount } =
    useMemo(() => {
      return rows.reduce(
        (acc, row) => {
          if (row.invoiced) {
            acc.invoicedTotal += row.amount_excl;
            acc.invoicedCount += 1;
          } else {
            acc.pendingTotal += row.amount_excl;
            acc.pendingCount += 1;
          }
          return acc;
        },
        { pendingTotal: 0, pendingCount: 0, invoicedTotal: 0, invoicedCount: 0 }
      );
    }, [rows]);

  const columns = useMemo<DataGridColumn<Row>[]>(
    () => [
      {
        id: 'organization_name',
        header: t('organization'),
        field: 'organization_name',
        accessorKey: 'organization_name',
        minWidth: 200,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'description',
        header: t('description'),
        field: 'description',
        accessorKey: 'description',
        minWidth: 250,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'amount_excl',
        header: t('amount'),
        field: 'amount_excl',
        accessorKey: 'amount_excl',
        maxWidth: 140,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'number',
        cell: ({ getValue }) =>
          formatCurrency(Number(getValue()) || 0, currency)
      },
      {
        id: 'invoiced',
        header: t('status'),
        field: 'invoiced',
        accessorKey: 'invoiced',
        maxWidth: 140,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'select',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: t('pending'), value: 'false' },
          { label: t('invoiced'), value: 'true' }
        ],
        cell: ({ getValue }) => {
          const isInvoiced = getValue() as boolean;
          return (
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium"
              style={{
                backgroundColor: isInvoiced
                  ? 'rgba(34, 197, 94, 0.1)'
                  : 'rgba(234, 179, 8, 0.1)',
                color: isInvoiced ? 'rgb(22, 163, 74)' : 'rgb(202, 138, 4)'
              }}
            >
              {isInvoiced && <CheckCircle2 className="h-3 w-3" />}
              {isInvoiced ? t('invoiced') : t('pending')}
            </span>
          );
        }
      },
      {
        id: 'created_by',
        header: t('created-by'),
        field: 'created_by',
        accessorKey: 'created_by',
        minWidth: 180,
        enableSorting: true,
        enableFiltering: true,
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'created_at',
        header: t('created-at'),
        field: 'created_at',
        accessorKey: 'created_at',
        minWidth: 140,
        enableSorting: true,
        enableFiltering: false,
        cell: ({ getValue }) => formatDate(String(getValue() ?? ''))
      }
    ],
    [t, currency]
  );

  const actions = useMemo<GridAction<Row>[]>(
    () => [
      {
        id: 'delete',
        label: t('delete'),
        icon: <Trash2 className="h-3.5 w-3.5" />,
        intent: 'danger',
        disabled: (row: Row) => row.invoiced,
        requiresConfirm: (row?: Row) => {
          if (!row || row.invoiced) return false;
          return {
            title: t('delete-confirmation'),
            body: t('are-you-sure-you-want-to-delete-this-one-off-charge'),
            confirmText: t('delete')
          };
        },
        run: async (row: Row) => {
          if (row.invoiced) {
            toast.error(t('cannot-delete-invoiced-charge'));
            return;
          }
          try {
            await deleteCharge(row.id);
            toast.success(t('one-off-charge-deleted-successfully'));
          } catch (e) {
            const err = e as AxiosError<BaseError>;
            toast.error(
              err?.response?.data?.error_description ??
                err?.response?.data?.message ??
                t('failed-to-delete-one-off-charge')
            );
          }
        }
      }
    ],
    [t, deleteCharge]
  );

  return (
    <div className="p-2 flex-1 min-h-0 flex flex-col">
      <AddOneOffModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={() => refetch()}
        currency={currency}
      />

      {/* Header */}
      <div className="w-full px-4 md:px-0 border-b border-hairline mb-4 pb-4">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <h1
                className="text-lg font-semibold"
                style={{ color: getColour('normalText') }}
              >
                {t('one-off-charges')}
              </h1>
              <p
                className="text-sm mt-0.5"
                style={{ color: getColour('inactiveText') }}
              >
                {t('manage-one-off-charges-description')}
              </p>
            </div>
            <Button
              onClick={() => setIsAddModalOpen(true)}
              label={t('add-one-off')}
              icon={<Plus className="h-4 w-4" />}
              size="small"
            />
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Pending Card */}
            <div className="rounded-2xl border border-hairline bg-app-soft shadow-card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p
                    className="text-xs font-medium uppercase tracking-wide"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('pending')}
                  </p>
                  <p
                    className="text-xl font-semibold mt-1"
                    style={{ color: 'rgb(202, 138, 4)' }}
                  >
                    {formatCurrency(pendingTotal, currency)}
                  </p>
                </div>
                <div
                  className="text-sm"
                  style={{ color: getColour('inactiveText') }}
                >
                  {pendingCount} {t('items')}
                </div>
              </div>
            </div>

            {/* Invoiced Card */}
            <div className="rounded-2xl border border-hairline bg-app-soft shadow-card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p
                    className="text-xs font-medium uppercase tracking-wide"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('invoiced')}
                  </p>
                  <p
                    className="text-xl font-semibold mt-1"
                    style={{ color: 'rgb(22, 163, 74)' }}
                  >
                    {formatCurrency(invoicedTotal, currency)}
                  </p>
                </div>
                <div
                  className="text-sm"
                  style={{ color: getColour('inactiveText') }}
                >
                  {invoicedCount} {t('items')}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Data Grid */}
      <div className="w-full p-2 md:p-0 flex-1 min-h-0 flex flex-col">
        {isLoading ? (
          <div className="h-96 w-full flex justify-center items-center">
            <Loader height={10} width={3} />
          </div>
        ) : (
          <DataGridV2<Row>
            data={rows}
            columns={columns}
            actions={actions}
            enableRowSelection={false}
            enablePagination
            manualPagination
            enableSorting
            manualSorting
            enableFiltering
            manualFiltering
            totalItems={chargesData?.total_results ?? 0}
            pageSize={PAGE_SIZE}
            pageSizeOptions={[25, 50, 75, 100]}
            showColumnFilters={false}
            filterMode="popover"
            fill
            dense
            grid={false}
            striped
            loading={isLoading}
            defaultSort={[{ id: 'created_at', desc: true }]}
            getRowId={row => String(row.id)}
            onPaginationChange={handlePaginationChange}
            onSortingChange={handleSortChange}
            onFilteringChange={handleFilterChange}
          />
        )}
      </div>
    </div>
  );
}
